import { useState } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { MoreVertRounded as MoreVertIcon } from '@material-ui/icons';
import moment from 'moment';

import { Quote } from '../../types/quote.type';
import { QuoteStatus } from '../../types/quote-status.type';
import { QuoteStatusChip } from '../Chips/QuoteStatusChip.component';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';

export type QuoteAction = 'edit' | 'duplicate' | 'download' | 'view-order';

type Props = {
  quotes: Quote[];
  isLoading: boolean;
  onAction: (action: QuoteAction, quote: Quote) => void;
  onStatusChange: (quote: Quote, nextStatus: QuoteStatus) => void | Promise<void>;
};

type QuotesTableHeader = {
  key: string;
  label?: string;
  minWidth: number;
  align: 'left' | 'right';
};

const QUOTES_TABLE_HEADERS: QuotesTableHeader[] = [
  { key: 'number', label: 'N° Devis', minWidth: 140, align: 'left' },
  { key: 'status', label: 'Statut', minWidth: 130, align: 'left' },
  { key: 'validUntil', label: 'Validité', minWidth: 130, align: 'left' },
  { key: 'discount', label: 'Remise', minWidth: 100, align: 'right' },
  { key: 'totalTtc', label: 'Total TTC', minWidth: 140, align: 'right' },
  { key: 'createdAt', label: 'Créé le', minWidth: 130, align: 'left' },
  { key: 'actions', minWidth: 65, align: 'right' },
];

const ACTION_LABELS: Record<QuoteAction, string> = {
  edit: 'Modifier',
  duplicate: 'Dupliquer',
  download: 'Télécharger le PDF',
  'view-order': 'Voir la commande',
};

const ACTIONS_BY_STATUS: Record<QuoteStatus, QuoteAction[]> = {
  DRAFT: ['edit'],
  READY: ['download'],
  SENT: ['download', 'duplicate'],
  ACCEPTED: ['view-order', 'download', 'duplicate'],
  REFUSED: ['download', 'duplicate'],
  EXPIRED: ['download', 'duplicate'],
  CANCELLED: ['download', 'duplicate'],
};

const DEFAULT_SKELETON_ROWS = 5;

const currencyFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });

function formatCurrency(amount: number): string {
  return currencyFormatter.format(amount);
}

function formatShortDate(date: string): string {
  return moment(date).format('DD/MM/YYYY');
}

function formatDiscount(quote: Quote): string {
  if (!quote.globalDiscount || quote.globalDiscount.value <= 0) return '—';
  if (quote.globalDiscount.reduction === 'percent') return `${quote.globalDiscount.value}%`;
  return formatCurrency(quote.globalDiscount.value);
}


function QuotesTableHeaderRow({ headers }: { headers: QuotesTableHeader[] }) {
  return (
    <TableHead>
      <TableRow>
        {headers.map((header) => (
          <TableCell key={header.key} align={header.align} style={{ minWidth: header.minWidth, fontWeight: 'bold' }}>
            {header?.label ?? ''}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function QuoteRowActions({ quote, onAction }: { quote: Quote; onAction: (action: QuoteAction, quote: Quote) => void }) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (action: QuoteAction) => {
    handleClose();
    onAction(action, quote);
  };

  const availableActions = (ACTIONS_BY_STATUS[quote.status] ?? []).filter(
    (action) => action !== 'download' || Boolean(quote.s3Key),
  );

  if (availableActions.length === 0) return null;

  return (
    <>
      <IconButton size="small" aria-controls="quote-actions" aria-haspopup="true" onClick={handleOpen}>
        <MoreVertIcon />
      </IconButton>
      <Menu id="quote-actions" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        {availableActions.map((action) => (
          <MenuItem key={action} onClick={() => handleSelect(action)}>
            {ACTION_LABELS[action]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function QuoteTableRow({
  quote,
  onAction,
  onStatusChange,
}: {
  quote: Quote;
  onAction: (action: QuoteAction, quote: Quote) => void;
  onStatusChange: (quote: Quote, nextStatus: QuoteStatus) => void | Promise<void>;
}) {
  return (
    <TableRow hover>
      <TableCell>{quote.number}</TableCell>
      <TableCell>
        <QuoteStatusChip status={quote.status} onChange={(next) => onStatusChange(quote, next)} />
      </TableCell>
      <TableCell>{formatShortDate(quote.validUntil)}</TableCell>
      <TableCell align="right">{formatDiscount(quote)}</TableCell>
      <TableCell align="right">{formatCurrency(quote.totals.totalTtc)}</TableCell>
      <TableCell>{formatShortDate(quote.createdAt)}</TableCell>
      <TableCell align="right">
        <QuoteRowActions quote={quote} onAction={onAction} />
      </TableCell>
    </TableRow>
  );
}

export function QuotesTable({ quotes, isLoading, onAction, onStatusChange }: Props) {
  return (
    <Paper>
      <TableContainer style={{ maxHeight: '75svh' }}>
        <Table stickyHeader>
          <QuotesTableHeaderRow headers={QUOTES_TABLE_HEADERS} />
          {isLoading && <TableBodySkeleton rows={DEFAULT_SKELETON_ROWS} columns={QUOTES_TABLE_HEADERS.length} />}
          {!isLoading && (
            <TableBody>
              {quotes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={QUOTES_TABLE_HEADERS.length} align="center">
                    Aucun devis
                  </TableCell>
                </TableRow>
              )}
              {quotes.map((quote) => (
                <QuoteTableRow
                  key={quote.id}
                  quote={quote}
                  onAction={onAction}
                  onStatusChange={onStatusChange}
                />
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
}
